import React, { useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { useLang } from "../i18n";
import { AttachmentInput } from "../components/FormLink";
import CountrySelect from "../components/CountrySelect";

const FIELDS = [
  { key: "legal_name", label: "s.legal_name", required: true },
  { key: "tax_id", label: "s.tax_id" },
  { key: "economic_sector", label: "s.economic_sector" },
  { key: "contact", label: "s.contact" },
  { key: "phone", label: "s.phone" },
  { key: "email", label: "s.email", type: "email", required: true },
];

export default function PublicKyp({ publicToken }) {
  const { t } = useLang();
  const [form, setForm] = useState({});
  const [atts, setAtts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/public/form/${publicToken}`, { data: form, attachments: atts });
      setDone(data.form_number);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="ec-card p-10 text-center space-y-3 max-w-xl mx-auto" data-testid="kyp-public-done">
        <ShieldCheck className="w-12 h-12 text-emerald-600 mx-auto" />
        <h2 className="font-heading text-xl font-extrabold text-slate-900">{t("forms.public_done")}</h2>
        <p className="text-sm text-slate-600">{t("forms.public_done_no")} <span className="font-mono font-bold">{done}</span></p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="ec-card p-6 space-y-5 max-w-3xl mx-auto" data-testid="kyp-public-form">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">{t("s.public_title")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("s.public_sub")}</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="ec-label">{t(f.label)}{f.required ? " *" : ""}</label>
            <input className="ec-input" type={f.type || "text"} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} required={f.required} data-testid={`kyp-field-${f.key}`} />
          </div>
        ))}
        <div>
          <label className="ec-label">{t("s.country")}</label>
          <CountrySelect value={form.country || ""} onChange={(v) => setForm({ ...form, country: v })} testid="kyp-field-country" />
        </div>
      </div>
      <AttachmentInput value={atts} onChange={setAtts} testid="kyp-attachments" />
      <button type="submit" disabled={busy} data-testid="kyp-public-submit" className="w-full px-4 py-3 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60 flex items-center justify-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("common.save")}
      </button>
    </form>
  );
}
